import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Modal } from './Modal';
import { Icon } from './Icon';
import { formatCurrency } from '../utils/dataUtils';
import { fetchInvoiceCSVText, describeDigitalOceanError } from '../utils/csvUtils';
import { formatPeriodLabel } from './charts/chartTheme';
import {
  REPORT_LIMITS,
  ReportError,
  submitReport,
  waitForReport,
  downloadReport,
  describeReportError
} from '../utils/reportApi';
import { validateCsvFile, findCsvProblem, formatFileSize, isSameFile } from '../utils/reportFiles';

const reportsAvailable = () =>
  !(typeof window !== 'undefined' && window.ENV_VARS && window.ENV_VARS.REPORTS_ENABLED === false);

const STATUS_TEXT = {
  queued: 'Waiting for the reporting service to start…',
  processing: 'The reporting service is building and checking the report…'
};

// Amounts in the service's response are USD strings such as "8124.26".
const formatUsd = (value) => {
  const amount = Number(value);
  if (!isFinite(amount)) return String(value);
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

let localFileCounter = 0;

// Lets the user pick invoice CSVs and/or CSV files from their device, sends
// them to the reporting service and offers the generated workbook.
export const GenerateReportModal = ({ isOpen, onClose, invoices = [], apiToken, accountName }) => {
  const [selectedInvoices, setSelectedInvoices] = useState([]); // invoice_uuid[]
  const [localFiles, setLocalFiles] = useState([]); // [{ id, file, problem, checking }]
  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [phase, setPhase] = useState('select'); // 'select' | 'working' | 'done' | 'error'
  const [progress, setProgress] = useState('');
  const [formError, setFormError] = useState('');
  const [job, setJob] = useState(null);
  const [failure, setFailure] = useState(null);
  const [download, setDownload] = useState({ busy: false, error: '', done: false });
  // Each run gets a number; a run whose number is no longer current has been cancelled
  const runRef = useRef(0);

  useEffect(() => () => { runRef.current += 1; }, []);

  const sortedInvoices = useMemo(
    () => [...invoices].sort((a, b) => String(b.invoice_period || '').localeCompare(String(a.invoice_period || ''))),
    [invoices]
  );

  const selectedCount = selectedInvoices.length + localFiles.length;
  const isWorking = phase === 'working';
  const available = reportsAvailable();

  const toggleInvoice = (uuid) => {
    setFormError('');
    setSelectedInvoices((current) =>
      current.includes(uuid) ? current.filter((id) => id !== uuid) : [...current, uuid]
    );
  };

  const selectAllInvoices = () => {
    setFormError('');
    setSelectedInvoices(sortedInvoices.map((invoice) => invoice.invoice_uuid));
  };

  const clearInvoices = () => setSelectedInvoices([]);

  const handleFilesChosen = (event) => {
    const chosen = Array.from(event.target.files || []);
    // Allow choosing the same file again after it was removed
    event.target.value = '';
    if (chosen.length === 0) return;

    const fresh = [];
    const repeated = [];
    chosen.forEach((file) => {
      const known = [...localFiles.map((entry) => entry.file), ...fresh.map((entry) => entry.file)];
      if (known.some((existing) => isSameFile(existing, file))) {
        repeated.push(file.name);
      } else {
        localFileCounter += 1;
        fresh.push({ id: `local-${localFileCounter}`, file, problem: null, checking: true });
      }
    });

    setFormError(
      repeated.length > 0
        ? `${repeated.join(', ')} ${repeated.length === 1 ? 'was' : 'were'} already added.`
        : ''
    );
    if (fresh.length === 0) return;
    setLocalFiles((current) => [...current, ...fresh]);

    fresh.forEach(async (entry) => {
      const problem = await validateCsvFile(entry.file);
      setLocalFiles((current) =>
        current.map((item) => (item.id === entry.id ? { ...item, problem, checking: false } : item))
      );
    });
  };

  const removeLocalFile = (id) => {
    setFormError('');
    setLocalFiles((current) => current.filter((entry) => entry.id !== id));
  };

  // Download each selected invoice's CSV from DigitalOcean and name it by month
  const prepareInvoiceFiles = async (isCancelled) => {
    const files = [];
    const usedNames = new Set(localFiles.map((entry) => entry.file.name));
    const chosen = sortedInvoices.filter((invoice) => selectedInvoices.includes(invoice.invoice_uuid));

    for (let index = 0; index < chosen.length; index += 1) {
      const invoice = chosen[index];
      const label = formatPeriodLabel(invoice.invoice_period || invoice.invoice_uuid);
      setProgress(`Fetching invoice ${index + 1} of ${chosen.length} from DigitalOcean (${label})…`);

      let text;
      try {
        text = await fetchInvoiceCSVText(apiToken, invoice.invoice_uuid);
      } catch (err) {
        throw new ReportError(
          `The ${label} invoice could not be fetched from DigitalOcean. ${describeDigitalOceanError(err)}`,
          { code: 'INVOICE_FETCH' }
        );
      }
      if (isCancelled()) return null;

      let name = `invoice_${invoice.invoice_period || invoice.invoice_uuid}.csv`;
      if (usedNames.has(name)) name = `invoice_${invoice.invoice_period}_${String(invoice.invoice_uuid).slice(0, 8)}.csv`;
      usedNames.add(name);

      const problem = findCsvProblem(`The ${label} invoice`, text);
      if (problem) throw new ReportError(problem, { code: 'INVALID_FILE' });
      files.push(new File([text], name, { type: 'text/csv' }));
    }
    return files;
  };

  const handleGenerate = async () => {
    if (isWorking) return; // one report at a time

    if (selectedCount === 0) {
      setFormError('Select at least one CSV file to generate a report.');
      return;
    }
    if (localFiles.some((entry) => entry.checking)) {
      setFormError('The selected files are still being checked. Try again in a moment.');
      return;
    }
    if (localFiles.some((entry) => entry.problem)) {
      setFormError('Remove the files marked with a problem before generating the report.');
      return;
    }
    if (selectedCount > REPORT_LIMITS.maxFiles) {
      setFormError(`A report can use up to ${REPORT_LIMITS.maxFiles} files. You selected ${selectedCount}.`);
      return;
    }

    runRef.current += 1;
    const run = runRef.current;
    const isCancelled = () => runRef.current !== run;

    setFormError('');
    setFailure(null);
    setJob(null);
    setDownload({ busy: false, error: '', done: false });
    setPhase('working');

    try {
      const invoiceFiles = await prepareInvoiceFiles(isCancelled);
      if (!invoiceFiles || isCancelled()) return;
      const files = [...invoiceFiles, ...localFiles.map((entry) => entry.file)];

      setProgress(`Uploading ${files.length} file${files.length === 1 ? '' : 's'} to the reporting service…`);
      const submitted = await submitReport(files, { title, company });
      if (isCancelled()) return;

      setProgress(STATUS_TEXT[submitted.status] || 'Generating the report…');
      const finished = await waitForReport(submitted, {
        isCancelled,
        onUpdate: (current) => {
          if (!isCancelled() && STATUS_TEXT[current.status]) setProgress(STATUS_TEXT[current.status]);
        }
      });
      if (!finished || isCancelled()) return;

      setJob(finished);
      setPhase('done');
    } catch (err) {
      if (isCancelled()) return;
      setFailure(describeReportError(err));
      setPhase('error');
    }
  };

  const handleCancelRun = () => {
    runRef.current += 1;
    setPhase('select');
    setProgress('');
  };

  const handleDownload = async () => {
    if (download.busy || !job || !job.report) return;
    setDownload({ busy: true, error: '', done: false });
    try {
      await downloadReport(job.report);
      setDownload({ busy: false, error: '', done: true });
    } catch (err) {
      setDownload({ busy: false, error: describeReportError(err).messages.join(' '), done: false });
    }
  };

  const backToSelection = () => {
    setPhase('select');
    setFailure(null);
  };

  const startOver = () => {
    setSelectedInvoices([]);
    setLocalFiles([]);
    setTitle('');
    setCompany('');
    setJob(null);
    setFormError('');
    setPhase('select');
  };

  // Escape, the backdrop and the close button do nothing while a report is
  // being generated, so it cannot be lost by accident. "Stop" is explicit.
  const handleClose = () => {
    if (isWorking) return;
    if (phase === 'done') startOver();
    if (phase === 'error') backToSelection();
    onClose();
  };

  const renderSelection = () => (
    <>
      {!available && (
        <div className="alert alert-warning" role="status">
          <span>
            Report generation is not set up on this server yet. An administrator needs to configure the
            reporting service before reports can be generated.
          </span>
        </div>
      )}

      <p className="settings-description">
        Choose the billing CSV files for the report: one file per invoice month, up to {REPORT_LIMITS.maxFiles} files.
        The reporting service returns an Excel workbook.
      </p>

      <section className="report-section" aria-labelledby="report-invoices-heading">
        <div className="report-section-header">
          <h3 id="report-invoices-heading">Invoices from {accountName}</h3>
          {sortedInvoices.length > 0 && (
            <span className="report-section-actions">
              <button type="button" className="link-button" onClick={selectAllInvoices}>Select all</button>
              <button type="button" className="link-button" onClick={clearInvoices} disabled={selectedInvoices.length === 0}>Clear</button>
            </span>
          )}
        </div>
        {sortedInvoices.length === 0 ? (
          <p className="report-empty">This account has no invoices yet. You can still add CSV files from your device.</p>
        ) : (
          <ul className="invoice-picker">
            {sortedInvoices.map((invoice) => {
              const id = `report-invoice-${invoice.invoice_uuid}`;
              return (
                <li key={invoice.invoice_uuid}>
                  <input
                    type="checkbox"
                    id={id}
                    checked={selectedInvoices.includes(invoice.invoice_uuid)}
                    onChange={() => toggleInvoice(invoice.invoice_uuid)}
                  />
                  <label htmlFor={id}>
                    <span>{formatPeriodLabel(invoice.invoice_period || invoice.invoice_uuid)}</span>
                    <span className="invoice-picker-amount">{formatCurrency(Number.parseFloat(invoice.amount) || 0)}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="report-section" aria-labelledby="report-files-heading">
        <div className="report-section-header">
          <h3 id="report-files-heading">CSV files from your device</h3>
          <label className="btn-secondary btn-small file-button">
            <input
              type="file"
              accept=".csv,text/csv"
              multiple
              className="visually-hidden"
              onChange={handleFilesChosen}
            />
            <Icon name="upload" size={14} />
            Add CSV files
          </label>
        </div>
        {localFiles.length === 0 ? (
          <p className="report-empty">
            No files added. Each file needs the columns {REPORT_LIMITS.requiredColumns.join(', ')} and can be up
            to {formatFileSize(REPORT_LIMITS.maxFileBytes)}.
          </p>
        ) : (
          <ul className="file-list">
            {localFiles.map((entry) => (
              <li key={entry.id} className={entry.problem ? 'file-row has-problem' : 'file-row'}>
                <Icon name={entry.problem ? 'alert' : 'file'} />
                <span className="file-row-main">
                  <span className="file-row-name">{entry.file.name}</span>
                  <span className="file-row-meta">
                    {entry.checking ? 'Checking…' : formatFileSize(entry.file.size)}
                  </span>
                  {entry.problem && <span className="field-error" role="alert">{entry.problem}</span>}
                </span>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeLocalFile(entry.id)}
                  aria-label={`Remove ${entry.file.name}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="report-section" aria-labelledby="report-details-heading">
        <div className="report-section-header">
          <h3 id="report-details-heading">Details (optional)</h3>
        </div>
        <div className="report-fields">
          <div className="form-group">
            <label htmlFor="reportTitle">Report title</label>
            <input
              id="reportTitle"
              type="text"
              value={title}
              maxLength={REPORT_LIMITS.maxTextLength}
              placeholder="Cloud Infrastructure Cost Report"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="reportCompany">Company</label>
            <input
              id="reportCompany"
              type="text"
              value={company}
              maxLength={REPORT_LIMITS.maxTextLength}
              placeholder="Taken from the CSV when left empty"
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>
        </div>
      </section>

      {formError && <div className="alert" role="alert"><span>{formError}</span></div>}
    </>
  );

  const renderWorking = () => (
    <div className="report-progress" role="status" aria-live="polite">
      <div className="loading-spinner" aria-hidden="true"></div>
      <p className="report-progress-title">Generating your report</p>
      <p className="report-progress-text">{progress}</p>
      <progress aria-label="Report generation in progress"></progress>
      <p className="report-progress-note">This usually takes a few seconds. Keep this window open.</p>
    </div>
  );

  const renderDone = () => {
    const report = job.report;
    const summary = report.summary || {};
    const period = report.period || {};
    const counts = (report.data_quality && report.data_quality.counts) || {};
    const reconciliationFailed = report.reconciliation && report.reconciliation.passed === false;
    const verificationFailed = report.verification && report.verification.passed === false;
    const duplicates = Array.isArray(report.duplicate_files) ? report.duplicate_files : [];
    const flags = [];
    if (reconciliationFailed) flags.push('the totals did not reconcile with the invoices');
    if (verificationFailed) flags.push('the workbook did not pass its checks');

    return (
      <div className="report-result">
        <div className="report-result-heading" role="status">
          <span className="report-result-icon"><Icon name="check" size={18} /></span>
          <div>
            <p className="report-result-title">Your report is ready</p>
            <p className="report-result-file">
              {report.file_name || 'report.xlsx'}
              {report.file_size_bytes ? ` · ${formatFileSize(report.file_size_bytes)}` : ''}
            </p>
          </div>
        </div>

        {flags.length > 0 && (
          <div className="alert alert-warning" role="alert">
            <span>
              The reporting service flagged this report: {flags.join(' and ')}. Review it before sharing.
            </span>
          </div>
        )}

        <dl className="report-facts">
          {report.title && (<><dt>Title</dt><dd>{report.title}</dd></>)}
          {report.company && (<><dt>Company</dt><dd>{report.company}</dd></>)}
          {period.start && period.end && (
            <><dt>Period</dt><dd>{period.start} to {period.end}{period.months ? ` (${period.months} month${period.months === 1 ? '' : 's'})` : ''}</dd></>
          )}
          {summary.net !== undefined && (<><dt>Net spend</dt><dd>{formatUsd(summary.net)}</dd></>)}
          {summary.gross !== undefined && (<><dt>Gross</dt><dd>{formatUsd(summary.gross)}</dd></>)}
          {summary.discount !== undefined && (<><dt>Discounts</dt><dd>{formatUsd(summary.discount)}</dd></>)}
          {summary.line_items !== undefined && (<><dt>Line items</dt><dd>{Number(summary.line_items).toLocaleString()}</dd></>)}
          {(counts.warning > 0 || counts.error > 0) && (
            <><dt>Data quality</dt><dd>{counts.error || 0} error{counts.error === 1 ? '' : 's'}, {counts.warning || 0} warning{counts.warning === 1 ? '' : 's'}</dd></>
          )}
          {duplicates.length > 0 && (
            <><dt>Duplicates ignored</dt><dd>{duplicates.map((entry) => (typeof entry === 'string' ? entry : entry.name)).join(', ')}</dd></>
          )}
        </dl>

        {report.expires_at && (
          <p className="report-progress-note">
            Download it now: the reporting service keeps it until {new Date(report.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.
          </p>
        )}

        {download.error && <div className="alert" role="alert"><span>{download.error}</span></div>}
        {download.done && <p className="report-download-done" role="status">The report was downloaded.</p>}
      </div>
    );
  };

  const renderError = () => (
    <div className="report-result">
      <div className="alert" role="alert">
        <div>
          <strong>The report could not be generated.</strong>
          <ul className="report-error-list">
            {failure.messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
          {failure.hint && <p className="report-error-hint">{failure.hint}</p>}
          {failure.requestId && (
            <p className="report-error-hint">Reference for support: <code>{failure.requestId}</code></p>
          )}
        </div>
      </div>
    </div>
  );

  let body;
  let footer;
  if (phase === 'working') {
    body = renderWorking();
    footer = (
      <div className="form-buttons">
        <button type="button" className="btn-secondary" onClick={handleCancelRun}>Stop</button>
      </div>
    );
  } else if (phase === 'done' && job) {
    body = renderDone();
    footer = (
      <div className="form-buttons">
        <button type="button" className="btn-secondary" onClick={startOver}>Generate another</button>
        <button type="button" onClick={handleDownload} disabled={download.busy}>
          <Icon name="download" />
          {download.busy ? 'Downloading...' : 'Download report'}
        </button>
      </div>
    );
  } else if (phase === 'error' && failure) {
    body = renderError();
    footer = (
      <div className="form-buttons">
        <button type="button" className="btn-secondary" onClick={backToSelection}>Back to files</button>
        <button type="button" onClick={handleGenerate}>Try again</button>
      </div>
    );
  } else {
    body = renderSelection();
    footer = (
      <div className="report-footer">
        <span className="report-count" role="status">
          {selectedCount === 0
            ? 'No files selected'
            : `${selectedCount} file${selectedCount === 1 ? '' : 's'} selected`}
        </span>
        <div className="form-buttons">
          <button type="button" className="btn-secondary" onClick={handleClose}>Cancel</button>
          <button type="button" onClick={handleGenerate} disabled={!available}>
            <Icon name="report" />
            Generate report
          </button>
        </div>
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Generate report" size="large" footer={footer}>
      {body}
    </Modal>
  );
};
